import React, { useMemo, useState } from 'react'
import { SubmitHandler, useFormContext } from 'react-hook-form'
import FilterList from '@/components/content/FilterList'
import DateRangePickerInput from '@/components/date/DateRangePickerInput'
import MultipleInput from '@/components/content/MultipleInput'
import SelectBoxStatus from '@/pages/outbound/outboundSearchOrder/components/SelectBoxStatus'

import { GetOutboundsReq, OutboundsStatusType } from '@/services/outbound/outboundSearch/types'
import { castArray, chunk } from 'lodash-es'
import dayjs from 'dayjs'
import { DATE_FORMAT } from '@/utils/consts'
import ShipperSelector from '@/components/system/ShipperSelector'
import { SHIPPER_TYPES } from '@/services/shipper/apis'

export const getInitForm = (parsedQs: Partial<GetOutboundsReq> = {}): Partial<GetOutboundsReq> => ({
  shipperNames: [],
  logisticsServiceTypes: parsedQs.logisticsServiceTypes ? castArray(parsedQs.logisticsServiceTypes) : SHIPPER_TYPES,
  cutDateFrom: parsedQs.cutDateFrom || dayjs().format(DATE_FORMAT.date),
  cutDateTo: parsedQs.cutDateTo || dayjs().format(DATE_FORMAT.date),
  actDateFrom: parsedQs.actDateFrom || undefined,
  actDateTo: parsedQs.actDateTo || undefined,
  orderNumbers: parsedQs.orderNumbers ? castArray(parsedQs.orderNumbers) : [],
  orderOptionNumbers: parsedQs.orderOptionNumbers ? castArray(parsedQs.orderOptionNumbers) : [],
  orderConsolNumbers: parsedQs.orderConsolNumbers ? castArray(parsedQs.orderConsolNumbers) : [],
  uids: parsedQs.uids ? castArray(parsedQs.uids) : [],
  barcodes: parsedQs.barcodes ? castArray(parsedQs.barcodes) : [],
  status: parsedQs.status || undefined,
  invoiceNumbers: parsedQs.invoiceNumbers ? castArray(parsedQs.invoiceNumbers) : [],
  salChannels: parsedQs.salChannels ? castArray(parsedQs.salChannels) : [],
})

interface Props {
  isLoading: boolean
  onSubmit: SubmitHandler<GetOutboundsReq>
  onFieldChange?: (params: { fieldName: keyof GetOutboundsReq; value: unknown; isSelectedAll?: boolean }) => void
  onReset?: () => void
}

function OutboundSearchOrderForm({ isLoading, onSubmit, onFieldChange, onReset }: Props) {
  const [dateRangeInvalid, setDateRangeInvalid] = useState(false)
  const { setValue, watch, reset, handleSubmit } = useFormContext<GetOutboundsReq>()
  const values = watch()

  const filterList = useMemo(
    () =>
      chunk(
        [
          {
            label: '화주명',
            required: true,
            validationValue: values.shipperNames && values.shipperNames.length > 0,
            value: <ShipperSelector logisticsServiceTypesWidth={120} shipperNamesWidth={250} onFieldChange={onFieldChange} />,
          },
          {
            label: '출고등록일',
            required: true,
            value: (
              <DateRangePickerInput
                value={[values.cutDateFrom, values.cutDateTo]}
                maxDateRange={13}
                onChange={([start, end]) => {
                  setValue('cutDateFrom', start ? dayjs(start).format(DATE_FORMAT.date) : '')
                  setValue('cutDateTo', end ? dayjs(end).format(DATE_FORMAT.date) : '')
                }}
              />
            ),
          },
          {
            label: '출고실적일',
            value: (
              <DateRangePickerInput
                value={[values.actDateFrom, values.actDateTo]}
                maxDateRange={13}
                onChange={([start, end]) => {
                  setValue('actDateFrom', start ? dayjs(start).format(DATE_FORMAT.date) : '')
                  setValue('actDateTo', end ? dayjs(end).format(DATE_FORMAT.date) : '')

                  if ((start && end) || (!start && !end)) {
                    setDateRangeInvalid(false)
                  } else {
                    setDateRangeInvalid(true)
                  }
                }}
              />
            ),
          },
          {
            label: '주문번호',
            value: (
              <MultipleInput modalTitle="주문번호" value={values.orderNumbers ?? []} onChange={(v) => setValue('orderNumbers', v)} maxSize={200} />
            ),
          },
          {
            label: '주문일련번호',
            value: (
              <MultipleInput
                modalTitle="주문일련번호"
                value={values.orderOptionNumbers ?? []}
                onChange={(v) => setValue('orderOptionNumbers', v)}
                maxSize={200}
              />
            ),
          },
          {
            label: '통합출고번호',
            value: (
              <MultipleInput
                modalTitle="통합출고번호"
                value={values.orderConsolNumbers ?? []}
                onChange={(v) => setValue('orderConsolNumbers', v)}
                maxSize={200}
              />
            ),
          },
          {
            label: 'UID',
            value: <MultipleInput modalTitle="UID" value={values.uids ?? []} onChange={(v) => setValue('uids', v)} maxSize={200} />,
          },
          {
            label: '바코드',
            value: <MultipleInput modalTitle="바코드" value={values.barcodes ?? []} onChange={(v) => setValue('barcodes', v)} maxSize={200} />,
          },
          {
            label: '출고상태',
            value: <SelectBoxStatus value={values.status} onChange={(v) => setValue('status', v as OutboundsStatusType)} />,
          },
          {
            label: '송장번호',
            value: (
              <MultipleInput
                modalTitle="송장번호"
                value={values.invoiceNumbers ?? []}
                onChange={(v) => setValue('invoiceNumbers', v)}
                maxSize={200}
              />
            ),
          },
          {
            label: '판매채널',
            value: (
              <MultipleInput modalTitle="판매채널" value={values.salChannels ?? []} onChange={(v) => setValue('salChannels', v)} maxSize={200} />
            ),
          },
        ],
        3,
      ),
    [values],
  )

  return (
    <FilterList
      isLoading={isLoading}
      list={filterList}
      onReset={onReset || (() => reset({ ...getInitForm() }))}
      onSearch={handleSubmit(onSubmit)}
      searchDisabled={dateRangeInvalid}
    />
  )
}

export default OutboundSearchOrderForm
